import React, { useState } from 'react';
import {
  Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  Grid, LinearProgress, Table, TableBody, TableCell, TableHead, TableRow,
  TextField, Typography, Alert, Pagination, ToggleButtonGroup, ToggleButton,
  Autocomplete, Checkbox,
} from '@mui/material';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ctaCampaignAPI, megaphoneAPI } from '../../services/api';
import Panel from '../crt/Panel';
import SectionTitle from '../crt/SectionTitle';

const STATUS_COLORS: Record<string, 'default'|'primary'|'secondary'|'error'|'info'|'success'|'warning'> = {
  draft: 'default', scheduled: 'info', applying: 'warning', active: 'success',
  removing: 'warning', completed: 'default', failed: 'error',
};

const CTACampaignsTab: React.FC = () => {
  const qc = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [detailId, setDetailId] = useState<number | null>(null);
  const [form, setForm] = useState({ name: '', cta_html: '', start_at: '', end_at: '', target_mode: 'all' as string, target_episode_ids: [] as string[] });
  const [message, setMessage] = useState<string | null>(null);

  const { data: campaigns } = useQuery({
    queryKey: ['cta-campaigns'],
    queryFn: ctaCampaignAPI.list,
    staleTime: 15000,
    refetchInterval: 30000,
  });

  const { data: detail } = useQuery({
    queryKey: ['cta-detail', detailId],
    queryFn: () => ctaCampaignAPI.get(detailId!),
    enabled: !!detailId,
    staleTime: 10000,
    refetchInterval: 15000,
  });

  const [epPage, setEpPage] = useState(1);
  const { data: episodes } = useQuery({
    queryKey: ['cta-episodes', detailId, epPage],
    queryFn: () => ctaCampaignAPI.episodes(detailId!, epPage),
    enabled: !!detailId,
    staleTime: 10000,
  });

  // All episodes for the picker
  const { data: allEpisodes } = useQuery({
    queryKey: ['meg-all-episodes'],
    queryFn: () => megaphoneAPI.getEpisodes({ limit: 500 }),
    staleTime: 120000,
    enabled: createOpen && form.target_mode === 'episodes',
  });

  const createMut = useMutation({
    mutationFn: (data: any) => ctaCampaignAPI.create(data),
    onSuccess: () => { setCreateOpen(false); setForm({ name: '', cta_html: '', start_at: '', end_at: '', target_mode: 'all', target_episode_ids: [] }); qc.invalidateQueries({ queryKey: ['cta-campaigns'] }); setMessage('Campaign created'); },
    onError: (e: any) => setMessage(`Error: ${e?.response?.data?.detail || e.message}`),
  });
  const scheduleMut = useMutation({
    mutationFn: (id: number) => ctaCampaignAPI.schedule(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['cta-campaigns'] }); qc.invalidateQueries({ queryKey: ['cta-detail'] }); setMessage('Campaign scheduled'); },
    onError: (e: any) => setMessage(`Error: ${e?.response?.data?.detail || e.message}`),
  });
  const applyMut = useMutation({
    mutationFn: (id: number) => ctaCampaignAPI.applyNow(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['cta-campaigns'] }); setMessage('Applying...'); },
    onError: (e: any) => setMessage(`Error: ${e?.response?.data?.detail || e.message}`),
  });
  const stopMut = useMutation({
    mutationFn: (id: number) => ctaCampaignAPI.stop(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['cta-campaigns'] }); setMessage('Stopping...'); },
    onError: (e: any) => setMessage(`Error: ${e?.response?.data?.detail || e.message}`),
  });
  const deleteMut = useMutation({
    mutationFn: (id: number) => ctaCampaignAPI.delete(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['cta-campaigns'] }); setDetailId(null); setMessage('Deleted'); },
    onError: (e: any) => setMessage(`Error: ${e?.response?.data?.detail || e.message}`),
  });
  const retryMut = useMutation({
    mutationFn: (id: number) => ctaCampaignAPI.retryFailed(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['cta-detail'] }); setMessage('Retrying failed episodes'); },
  });

  const campaignList = campaigns?.campaigns || [];

  return (
    <Box>
      <SectionTitle>CTA CAMPAIGNS</SectionTitle>

      {message && (
        <Alert severity={message.startsWith('Error') ? 'error' : 'success'} onClose={() => setMessage(null)} sx={{ mb: 2 }}>
          {message}
        </Alert>
      )}

      {/* Campaign list */}
      <Panel label="CAMPAIGNS">
        <Box display="flex" justifyContent="flex-end" mb={2}>
          <Button variant="outlined" onClick={() => setCreateOpen(true)}>[ NEW CAMPAIGN ]</Button>
        </Box>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>NAME</TableCell>
              <TableCell>STATUS</TableCell>
              <TableCell>DATES</TableCell>
              <TableCell>PROGRESS</TableCell>
              <TableCell>ACTIONS</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {campaignList.length === 0 && (
              <TableRow><TableCell colSpan={5}>No campaigns yet. Create one to get started.</TableCell></TableRow>
            )}
            {campaignList.map((c: any) => {
              const progress = c.episode_count > 0 ? Math.round((c.applied_count / c.episode_count) * 100) : 0;
              return (
                <TableRow key={c.id} hover sx={{ cursor: 'pointer' }} onClick={() => setDetailId(c.id)}>
                  <TableCell>
                    <Typography fontWeight={600}>{c.name}</Typography>
                    <Typography variant="caption" color="textSecondary">{c.marker_id}</Typography>
                  </TableCell>
                  <TableCell><Chip label={c.status} size="small" color={STATUS_COLORS[c.status] || 'default'} /></TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <Typography variant="caption">
                      {c.start_at ? new Date(c.start_at).toLocaleDateString() : '?'} → {c.end_at ? new Date(c.end_at).toLocaleDateString() : '?'}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ minWidth: 160 }}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <LinearProgress variant="determinate" value={progress} sx={{ flex: 1, height: 8 }} />
                      <Typography variant="caption">{c.applied_count}/{c.episode_count}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" gap={1} onClick={(e) => e.stopPropagation()}>
                      {c.status === 'draft' && (
                        <>
                          <Button size="small" onClick={() => scheduleMut.mutate(c.id)}>[ SCHEDULE ]</Button>
                          <Button size="small" onClick={() => applyMut.mutate(c.id)}>[ APPLY NOW ]</Button>
                          <Button size="small" color="error" onClick={() => { if (window.confirm('Delete this draft?')) deleteMut.mutate(c.id); }}>[ DELETE ]</Button>
                        </>
                      )}
                      {c.status === 'scheduled' && (
                        <>
                          <Button size="small" onClick={() => applyMut.mutate(c.id)}>[ APPLY NOW ]</Button>
                          <Button size="small" onClick={() => stopMut.mutate(c.id)}>[ CANCEL ]</Button>
                        </>
                      )}
                      {(c.status === 'applying' || c.status === 'active') && (
                        <Button size="small" color="error" onClick={() => { if (window.confirm('Stop and remove CTA from all episodes?')) stopMut.mutate(c.id); }}>[ STOP ]</Button>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Panel>

      {/* Detail view */}
      {detailId && detail && (
        <Panel label={`CAMPAIGN :: ${detail.name?.toUpperCase()}`} sx={{ mt: 3 }}>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid size={{ xs: 12, md: 4 }}>
              <Typography variant="caption" color="textSecondary">STATUS</Typography>
              <Box><Chip label={detail.status} color={STATUS_COLORS[detail.status] || 'default'} /></Box>
            </Grid>
            <Grid size={{ xs: 6, md: 2 }}>
              <Typography variant="caption" color="textSecondary">EPISODES</Typography>
              <Typography variant="h5" fontWeight={700}>{detail.episode_count}</Typography>
            </Grid>
            <Grid size={{ xs: 6, md: 2 }}>
              <Typography variant="caption" color="textSecondary">APPLIED</Typography>
              <Typography variant="h5" fontWeight={700}>{detail.applied_count}</Typography>
            </Grid>
            <Grid size={{ xs: 6, md: 2 }}>
              <Typography variant="caption" color="textSecondary">REMOVED</Typography>
              <Typography variant="h5" fontWeight={700}>{detail.removed_count}</Typography>
            </Grid>
            <Grid size={{ xs: 6, md: 2 }}>
              {detail.episode_statuses && Object.entries(detail.episode_statuses).some(([k, v]: any) => k.includes('failed') && v > 0) && (
                <Button size="small" variant="outlined" color="warning" onClick={() => retryMut.mutate(detailId)}>
                  [ RETRY FAILED ]
                </Button>
              )}
            </Grid>
          </Grid>
          <Typography variant="caption" color="textSecondary">CTA HTML:</Typography>
          <Box component="pre" sx={{ p: 1.5, border: '1px solid var(--crt-border)', backgroundColor: 'var(--crt-bg)', fontSize: '14px', whiteSpace: 'pre-wrap', maxHeight: 150, overflow: 'auto', mb: 2 }}>
            {detail.cta_html}
          </Box>

          {/* Episode status table */}
          <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 1 }}>EPISODE STATUS:</Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>EPISODE</TableCell>
                <TableCell>STATUS</TableCell>
                <TableCell>APPLIED</TableCell>
                <TableCell>REMOVED</TableCell>
                <TableCell>ERROR</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(episodes?.episodes || []).map((ep: any) => (
                <TableRow key={ep.id}>
                  <TableCell>
                    <Typography variant="body2" noWrap sx={{ maxWidth: 300 }}>{ep.episode_title}</Typography>
                  </TableCell>
                  <TableCell><Chip label={ep.status} size="small" color={STATUS_COLORS[ep.status] || 'default'} /></TableCell>
                  <TableCell>{ep.applied_at ? new Date(ep.applied_at).toLocaleString() : '—'}</TableCell>
                  <TableCell>{ep.removed_at ? new Date(ep.removed_at).toLocaleString() : '—'}</TableCell>
                  <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>{ep.last_error || ''}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {(episodes?.pages || 1) > 1 && (
            <Box display="flex" justifyContent="center" mt={1}>
              <Pagination count={episodes.pages} page={epPage} onChange={(_, p) => setEpPage(p)} size="small" />
            </Box>
          )}
          <Button size="small" sx={{ mt: 2 }} onClick={() => setDetailId(null)}>[ CLOSE ]</Button>
        </Panel>
      )}

      {/* Create dialog */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Create CTA Campaign</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField label="Campaign Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} fullWidth />
            <TextField
              label="CTA HTML" value={form.cta_html}
              onChange={(e) => setForm({ ...form, cta_html: e.target.value })}
              multiline rows={5} fullWidth
              helperText="HTML block to prepend to every episode description. Will be wrapped in sentinel markers."
              InputProps={{ sx: { fontFamily: "'Share Tech Mono', monospace", fontSize: '14px' } }}
            />
            <Box display="flex" gap={2}>
              <TextField label="Start Date" type="datetime-local" value={form.start_at} onChange={(e) => setForm({ ...form, start_at: e.target.value })} fullWidth InputLabelProps={{ shrink: true }} />
              <TextField label="End Date" type="datetime-local" value={form.end_at} onChange={(e) => setForm({ ...form, end_at: e.target.value })} fullWidth InputLabelProps={{ shrink: true }} />
            </Box>
            <Box>
              <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 1 }}>TARGET:</Typography>
              <ToggleButtonGroup
                exclusive
                value={form.target_mode}
                onChange={(_, v) => v && setForm({ ...form, target_mode: v, target_episode_ids: [] })}
                size="small"
              >
                <ToggleButton value="all">ALL EPISODES</ToggleButton>
                <ToggleButton value="episodes">SPECIFIC EPISODES</ToggleButton>
              </ToggleButtonGroup>
            </Box>
            {form.target_mode === 'episodes' && (
              <Autocomplete
                multiple
                options={(allEpisodes?.episodes || []).map((ep: any) => ({ id: ep.id, label: `S${ep.season_number || '?'}E${ep.episode_number || '?'} — ${ep.title}` }))}
                disableCloseOnSelect
                getOptionLabel={(o: any) => o.label}
                value={(allEpisodes?.episodes || []).filter((ep: any) => form.target_episode_ids.includes(ep.id)).map((ep: any) => ({ id: ep.id, label: `S${ep.season_number || '?'}E${ep.episode_number || '?'} — ${ep.title}` }))}
                onChange={(_, selected) => setForm({ ...form, target_episode_ids: selected.map((s: any) => s.id) })}
                renderOption={(props, option: any, { selected }) => (
                  <li {...props}>
                    <Checkbox icon={<CheckBoxOutlineBlankIcon fontSize="small" />} checkedIcon={<CheckBoxIcon fontSize="small" />} checked={selected} sx={{ mr: 1 }} />
                    {option.label}
                  </li>
                )}
                renderInput={(params) => <TextField {...params} label="Select Episodes" placeholder="Search episodes..." />}
                sx={{ maxHeight: 300 }}
              />
            )}
            {form.target_mode === 'episodes' && form.target_episode_ids.length > 0 && (
              <Typography variant="caption" color="textSecondary">
                {form.target_episode_ids.length} episode(s) selected
              </Typography>
            )}
            {form.cta_html && (
              <Box>
                <Typography variant="caption" color="textSecondary">PREVIEW:</Typography>
                <Box sx={{ p: 2, border: '1px solid var(--crt-border)', backgroundColor: '#1a1a1a' }}>
                  <Box sx={{ borderLeft: '3px solid #00ff41', pl: 1.5, mb: 1 }}>
                    <div dangerouslySetInnerHTML={{ __html: form.cta_html }} />
                  </Box>
                  <Typography variant="caption" color="textSecondary">
                    ↑ This block will appear at the TOP of every episode description.
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>[ CANCEL ]</Button>
          <Button
            variant="outlined"
            disabled={!form.name || !form.cta_html || !form.start_at || !form.end_at}
            onClick={() => createMut.mutate({
              name: form.name,
              cta_html: form.cta_html,
              start_at: new Date(form.start_at).toISOString(),
              end_at: new Date(form.end_at).toISOString(),
              target_mode: form.target_mode,
              target_episode_ids: form.target_mode === 'episodes' ? form.target_episode_ids : undefined,
            })}
          >
            [ CREATE DRAFT ]
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CTACampaignsTab;
